import { z } from "zod";

import { exactDecimalSchema } from "./faultContract";
import { formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";

// The elements of a revision's single-line diagram for the study pickers (SLD-b1, Master
// Prompt A27 (e)): GET /projects/{project}/revisions/{revision}/sld/elements answers every
// code with its category, kind, voltage, bus and upstream paths (primary first, the layout's
// rule), and each bus with the incomers, outgoing devices and loads around it. Version 0
// means the revision has no diagram yet: the lists are empty and the forms keep their typed
// codes. The backend mirror is app/services/sld_link.py and app/api/v1/sld.py.

const PROJECTS = "/api/v1/projects";
const UNEXPECTED = "Unexpected response from the KES Electrical OS single-line diagram API.";

export const SLD_ELEMENT_CATEGORIES = [
  "bus",
  "source",
  "transformer",
  "feeder",
  "incomer",
  "load",
  "device",
  "cable",
] as const;

export const sldElementCategorySchema = z.enum(SLD_ELEMENT_CATEGORIES);

export const sldElementSchema = z
  .object({
    code: z.string().min(1),
    category: sldElementCategorySchema,
    kind: z.string().nullable(),
    voltage_v: exactDecimalSchema.nullable(),
    bus: z.string().nullable(),
    // A load's rated kW, a device's A, a transformer's kVA: a descriptor for the prefill.
    rating: exactDecimalSchema.nullable().optional(),
    upstream_paths: z.array(z.array(z.string())).nullable(),
  })
  .strict();

export const sldBusSummarySchema = z
  .object({
    code: z.string().min(1),
    voltage_v: exactDecimalSchema,
    incomers: z.array(z.string()),
    outgoing_devices: z.array(z.string()),
    loads: z.array(z.string()),
    // Every load below the bus, directly or through lower buses (a load demand study's board).
    downstream_loads: z.array(z.string()).optional(),
  })
  .strict();

export const sldElementsSchema = z
  .object({
    project_revision_id: z.string().uuid(),
    version: z.number().int().nonnegative(),
    content_hash: z.string().nullable(),
    elements: z.array(sldElementSchema),
    buses: z.array(sldBusSummarySchema),
  })
  .strict();

export type SldElementCategory = z.infer<typeof sldElementCategorySchema>;
export type SldElement = z.infer<typeof sldElementSchema>;
export type SldBusSummary = z.infer<typeof sldBusSummarySchema>;
export type SldElements = z.infer<typeof sldElementsSchema>;

/** The elements of the revision's diagram; empty lists at version 0. */
export async function getSldElements(
  projectId: string,
  revisionId: string,
  signal?: AbortSignal,
): Promise<SldElements> {
  const project = encodeURIComponent(projectId);
  const revision = encodeURIComponent(revisionId);
  const response = await fetch(`${PROJECTS}/${project}/revisions/${revision}/sld/elements`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    credentials: "same-origin",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      formatApiError(data, response.status, "Single-line diagram elements"),
      response.status,
    );
  }

  const parsed = sldElementsSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error(UNEXPECTED);
  }

  return parsed.data;
}
